/**
 * Main Salesforce Service
 * Orchestrates authentication, conversation, and EventSource services
 */

import { SALESFORCE_EVENT_TYPES, CONVERSATION_STATUS, CONNECTION_STATUS } from './constants';
import { 
  initializeAuthentication, 
  getCurrentJwt, 
  clearAuthentication
} from './authService';
import { 
  createConversation, 
  sendMessage as conversationSendMessage, 
  sendTypingIndicator, 
  hasActiveConversation,
  getCurrentConversationId
} from './conversationService';
import { 
  subscribeToEventSource, 
  closeEventSource, 
  getConnectionStatus,
  isConnected
} from './eventSourceService';
import { clearSalesforceWebStorage, clearLastEventId, getLastEventId } from './webStorageService';
import networkMonitorService from './networkMonitorService';
import { SUPPORT_CHAT_NETWORK_LISTENER_ID } from '../types/constants';

/**
 * Salesforce Service Class
 * Main interface for Salesforce functionality
 */
class SalesforceService {
  constructor() {
    this.isInitialized = false;
    this.eventListeners = new Map();
    this.connectionStatusCallback = null;
    this.conversationStatus = CONVERSATION_STATUS.NOT_STARTED;
    this.needsFreshConnection = false; // Track if next connection should be fresh
  }

  /**
   * Initialize Salesforce service with configuration
   * 
   * ⚠️ CRITICAL: This function does NOT call initializeAuthentication anymore!
   * Why? initializeAuthentication always clears JWT, destroying page reload capability.
   * 
   * Instead:
   * - Just stores the config and marks service as initialized
   * - Component will check JWT and decide which token API to call
   * - If JWT exists → Component calls getContinuityJwt (page reload)
   * - If NO JWT → Component calls getUnauthenticatedAccessToken (new conversation)
   */
  async initialize(config = null) {
    try {
      // Store configuration if provided
      if (config) {
        const { storeSalesforceConfig } = await import('./webStorageService');
        await storeSalesforceConfig(config);
      }

      // Do NOT call initializeAuthentication here
      // Let the component decide which token API to call based on JWT existence
      
      this.isInitialized = true;
      
      return {
        success: true,
        message: 'Service initialized. Component should check JWT and call appropriate token API.'
      };
    } catch (error) {
      console.error('[Salesforce:Service] Failed to initialize Salesforce service:', error);
      this.isInitialized = false;
      throw error;
    }
  }

  /**
   * Start a new conversation with optional routing attributes
   * 
   * ============================================================================
   * AUTHENTICATED USERS FLOW (Cross-Device Support via JWT sub)
   * ============================================================================
   * 
   * STEP 1: AUTHENTICATION
   * - Receives userContext (userId, email, name) from component
   * - Gets Cognito IdToken (contains sub claim = userId)
   * - Exchanges for Salesforce Messaging Access Token via:
   *   POST /iamessage/api/v2/authorization/authenticated/access-token
   * - Salesforce validates JWT using Cognito JWKS
   * - Returns Messaging Token with embedded user identity (Cognito sub)
   * 
   * STEP 2: CHECK FOR EXISTING CONVERSATION (Cross-Device Lookup)
   * - Calls GET /iamessage/api/v2/conversation/list
   * - Salesforce returns ALL conversations for this verified user (Cognito sub)
   * - We find the most recent ACTIVE conversation
   * 
   * STEP 3a: IF HAS ACTIVE CONVERSATION → RESUME
   * - Use existing conversationId
   * - Fetch history via GET /conversation/{id}/entries
   * - User sees all previous messages (cross-device!)
   * 
   * STEP 3b: IF NO ACTIVE CONVERSATION → CREATE NEW
   * - POST /iamessage/api/v2/conversation with routingAttributes
   * - Pass user profile (email, name, dealerId) via routingAttributes
   * - These appear in agent console for context
   * 
   * KEY INSIGHT (Identity vs Profile):
   * - Identity: Cognito sub claim → Salesforce verified user
   * - Same Cognito sub on any device = same user = same conversations
   * - Profile: routingAttributes → Agent sees email, company, etc.
   * 
   * @param {Object} userContext - User identity from Cognito (userId, email, name)
   * @param {Object} routingAttributes - Additional routing attributes for agent assignment
   * @param {string} previousConversationId - Optional: Previous conversation ID to create new session for
   * @param {boolean} skipHistory - Optional: Skip fetching history (for fresh start after ENDED)
   * @returns {Promise<Object>} { conversationId, isExisting, existingMessages? }
   */
  async startConversation(userContext = null, routingAttributes = {}, previousConversationId = null, skipHistory = false) {
    try {
      if (!this.isInitialized) {
        throw new Error('Salesforce service not initialized');
      }
      
      // Validate user context for authenticated sessions
      if (!userContext || !userContext.userId) {
        throw new Error('User context with userId is required for authenticated conversations');
      }
      
      console.log('[Salesforce:Service] Starting conversation for user:', userContext.userId);
      
      // Store user context for conversation continuity
      const { storeUserContext, clearJwt, getJwt } = await import('./webStorageService');
      storeUserContext(userContext);
      
      // STEP 1: Get authenticated access token using Cognito JWT
      console.log('[Salesforce:Service] Step 1: Getting authenticated access token...');
      
      // Clear any existing token to ensure we get a fresh one
      // This prevents issues with stale tokens from previous sessions
      const existingJwt = getJwt();
      if (existingJwt) {
        console.log('[Salesforce:Service] Clearing existing JWT to get fresh token');
        clearJwt();
      }
      
      const { getAuthenticatedAccessToken, clearTokenCache } = await import('./authService');
      
      // Clear any cached token request
      if (clearTokenCache) {
        clearTokenCache();
      }
      
      const authResult = await getAuthenticatedAccessToken();
      
      if (!authResult?.accessToken) {
        throw new Error('Failed to get access token from authentication');
      }
      
      console.log('[Salesforce:Service] Auth result received, token length:', authResult.accessToken.length);
      
      // Verify the token is stored and matches what we received
      const storedJwt = getJwt();
      if (!storedJwt) {
        throw new Error('Token was not stored after authentication');
      }
      
      if (storedJwt !== authResult.accessToken) {
        console.error('[Salesforce:Service] Token mismatch! Stored token differs from auth result');
        console.error('[Salesforce:Service] Stored (first 50):', storedJwt.substring(0, 50));
        console.error('[Salesforce:Service] Auth result (first 50):', authResult.accessToken.substring(0, 50));
      }
      
      // Use getCurrentJwt to verify the flow
      const jwt = getCurrentJwt();
      if (!jwt) {
        throw new Error('Failed to obtain JWT token for conversation creation');
      }
      console.log('[Salesforce:Service] Step 1 complete: Got Salesforce messaging token');
      console.log('[Salesforce:Service] Token first 50 chars:', jwt.substring(0, 50) + '...');
      
      // STEP 2: Check for existing conversation (cross-device support)
      // For authenticated users, Salesforce links conversations to verified user (Cognito sub)
      // We use listConversations to find any existing ACTIVE conversation for this user
      console.log('[Salesforce:Service] Step 2: Checking for existing conversations...');
      
      const { findUserConversation, getConversationEntries, transformConversationEntries, parseEntriesForRedux } = 
        await import('./conversationService');
      
      let conversationResult;
      let isExistingConversation = false;
      
      // Check if we have a previousConversationId from restart flow
      if (previousConversationId) {
        console.log('[Salesforce:Service] Step 2: Using provided previousConversationId:', previousConversationId);
        
        // Check conversation status
        try {
          const { getConversationStatus } = await import('./conversationService');
          const statusResponse = await getConversationStatus(previousConversationId);
          console.log('[Salesforce:Service] Previous conversation status:', statusResponse);
        } catch (statusError) {
          console.warn('[Salesforce:Service] Could not check previous conversation status:', statusError);
        }
        
        console.log('[Salesforce:Service] Creating new session for previous conversation');
        
        // Create new session with the old conversationId
        conversationResult = await createConversation(routingAttributes, previousConversationId);
        
        // 🆕 Fetch history only if not skipHistory (fresh start after ENDED should skip)
        if (!skipHistory) {
          try {
            const { fetchRecentConversationsHistory } = await import('./conversationService');
            const historyResult = await fetchRecentConversationsHistory();
            
            conversationResult.existingMessages = historyResult.messages || [];
            console.log('[Salesforce:Service] 📚 Loaded', conversationResult.existingMessages.length, 
              'messages from', historyResult.conversationDetails?.length || 0, 'recent conversations');
          } catch (historyError) {
            console.warn('[Salesforce:Service] Could not fetch recent conversations history:', historyError);
            conversationResult.existingMessages = [];
          }
        } else {
          console.log('[Salesforce:Service] 🚫 Skipping history fetch - fresh start after ENDED conversation');
          conversationResult.existingMessages = [];
        }
      } else {
        // Normal flow: check for existing conversations
        console.log('[Salesforce:Service] Step 2: Checking for existing conversations...');
      
      try {
        const userConversation = await findUserConversation();
        
        if (userConversation.hasConversation) {
          // Found existing conversation - just resume it without creating new session
          console.log('[Salesforce:Service] Found existing conversation:', userConversation.conversationId);
          
          // Check conversation status
          let conversationStatus = 'ACTIVE';
          let routingStatus = null;
          try {
            const { getConversationStatus } = await import('./conversationService');
            console.log('[Salesforce:Service] Calling getConversationStatus API for:', userConversation.conversationId);
            const statusResponse = await getConversationStatus(userConversation.conversationId);
            console.log('[Salesforce:Service] ✅ Conversation status response:', statusResponse);
            // Use routingStatus for input control (INITIAL, ROUTED, etc.)
            // Use status for conversation state (ACTIVE, ENDED, etc.)
            conversationStatus = statusResponse.status || 'ACTIVE';
            routingStatus = statusResponse.routingStatus || null;
            console.log('[Salesforce:Service] Status:', conversationStatus, 'RoutingStatus:', routingStatus);
          } catch (statusError) {
            console.warn('[Salesforce:Service] Could not check conversation status:', statusError);
          }
          
          console.log('[Salesforce:Service] Resuming existing conversation without calling createConversation API');
          
          // Store conversation ID for sending messages
          const { storeConversationId } = await import('./webStorageService');
          storeConversationId(userConversation.conversationId);
          console.log('[Salesforce:Service] ✅ Stored conversationId:', userConversation.conversationId);

          // Resume existing conversation without calling create API
          isExistingConversation = true;
          conversationResult = {
            conversationId: userConversation.conversationId,
            isExisting: true,
            status: conversationStatus,
            conversationsCount: userConversation.totalConversationsCount || 0
          };
          
          // Load existing messages from conversation history
          conversationResult.existingMessages = userConversation.messages || [];
          conversationResult.conversationStatus = conversationStatus;
          
          // Check routingStatus (not status) to determine if input should be disabled
          // routingStatus indicates whether conversation is routed to an agent:
          // - NEEDS_ROUTING: No routing in progress, needs to be routed
          // - INITIAL: Initial routing in progress
          // - TRANSFER: Transfer routing in progress
          // - ROUTED: Agent is assigned, input should be enabled
          const statusToCheck = routingStatus || conversationStatus;
          const statusUpper = statusToCheck.toUpperCase();
          const needsRouting = statusUpper === 'NEEDS_ROUTING' || 
                              statusUpper === 'INITIAL' || 
                              statusUpper === 'TRANSFER' ||
                              statusUpper === 'WAITING' ||
                              statusUpper === 'CREATED';
          
          if (needsRouting) {
            // RoutingStatus is not ROUTED - need to create new session with old conversationId
            console.log('[Salesforce:Service] RoutingStatus requires routing:', statusToCheck, '- Creating new session with existing conversationId');
            
            // Call createConversation API with existing conversationId to create new session
            try {
              conversationResult = await createConversation(routingAttributes, userConversation.conversationId);
              console.log('[Salesforce:Service] ✅ New session created with existing conversationId');
              
              // Store conversation ID after creating new session
              const { storeConversationId } = await import('./webStorageService');
              storeConversationId(userConversation.conversationId);
              console.log('[Salesforce:Service] ✅ Stored conversationId:', userConversation.conversationId);
            } catch (createError) {
              console.error('[Salesforce:Service] Failed to create new session:', createError);
              // If create fails, still use the existing conversation and store ID
              conversationResult = {
                conversationId: userConversation.conversationId,
                isExisting: true,
                status: conversationStatus
              };
            }
            
            // Load existing messages and set waiting flag
            conversationResult.existingMessages = userConversation.messages || [];
            conversationResult.waitingForAgent = true;
            conversationResult.routingStatus = routingStatus;
            // Preserve conversationsCount from userConversation
            conversationResult.conversationsCount = userConversation.totalConversationsCount || 0;
            console.log('[Salesforce:Service] Input will be disabled, no waiting screen. Loaded', conversationResult.existingMessages.length, 'messages, conversationsCount:', conversationResult.conversationsCount);
          } else if (statusUpper === 'ROUTED') {
            // RoutingStatus is ROUTED - agent assigned, just resume without creating new session
            conversationResult.waitingForAgent = false;
            conversationResult.routingStatus = routingStatus;
            console.log('[Salesforce:Service] RoutingStatus is ROUTED - Input will be enabled');
          } else {
            // Unknown status - store routingStatus
            conversationResult.routingStatus = routingStatus;
          }
          
          console.log('[Salesforce:Service] Loaded', conversationResult.existingMessages.length, 'messages from history. Status:', conversationStatus);
        } else {
          // No active conversation found
          console.log('[Salesforce:Service] No active conversation found');
          
          // Check if there was a previous conversation (closed/ended)
          const previousConversationId = userConversation.previousConversationId;
          
          if (previousConversationId) {
            console.log('[Salesforce:Service] Found previous conversation ID:', previousConversationId);
            console.log('[Salesforce:Service] Creating new conversation session with existing conversation ID');
            // Pass the previous conversationId to create a new session for it
            conversationResult = await createConversation(routingAttributes, previousConversationId);
            // Add conversationsCount from userConversation
            conversationResult.conversationsCount = userConversation.totalConversationsCount || 0;
            
            // 🆕 ALWAYS fetch history from 2 most recent conversations
            try {
              const { fetchRecentConversationsHistory } = await import('./conversationService');
              const historyResult = await fetchRecentConversationsHistory();
              
              conversationResult.existingMessages = historyResult.messages || [];
              console.log('[Salesforce:Service] 📚 Loaded', conversationResult.existingMessages.length, 
                'messages from', historyResult.conversationDetails?.length || 0, 'recent conversations');
            } catch (historyError) {
              console.warn('[Salesforce:Service] Could not fetch recent conversations history:', historyError);
              conversationResult.existingMessages = [];
            }
          } else {
            // Truly new conversation - no previous conversation exists
            console.log('[Salesforce:Service] No previous conversation found - creating brand new conversation');
            conversationResult = await createConversation(routingAttributes);
            // No previous conversations
            conversationResult.conversationsCount = 0;
            
            // 🆕 Even for brand new conversation, try to fetch history from any previous conversations
            try {
              const { fetchRecentConversationsHistory } = await import('./conversationService');
              const historyResult = await fetchRecentConversationsHistory();
              
              if (historyResult.messages?.length > 0) {
                conversationResult.existingMessages = historyResult.messages;
                console.log('[Salesforce:Service] 📚 Loaded', historyResult.messages.length, 
                  'messages from', historyResult.conversationDetails?.length || 0, 'previous conversations');
              } else {
                // No history found - leave undefined to show waiting screen
                console.log('[Salesforce:Service] No previous history found');
              }
            } catch (historyError) {
              console.warn('[Salesforce:Service] Could not fetch previous conversations history:', historyError);
              // Leave existingMessages undefined to show waiting screen
            }
          }
          
          console.log('[Salesforce:Service] Conversation created/resumed:', conversationResult.conversationId);
        }
      } catch (lookupError) {
        // If lookup fails, fall back to creating new conversation
        console.error('[Salesforce:Service] findUserConversation FAILED!');
        console.error('[Salesforce:Service] Error type:', lookupError.name);
        console.error('[Salesforce:Service] Error message:', lookupError.message);
        console.error('[Salesforce:Service] Full error:', lookupError);
        
        // Check what token we have at this point
        const tokenAfterError = getJwt();
        console.error('[Salesforce:Service] Token after error:', tokenAfterError ? `present (${tokenAfterError.length} chars)` : 'MISSING');
        
        console.log('[Salesforce:Service] Fallback: Creating new conversation...');
        // Try to get previous conversation ID even if lookup failed
        const storedConversationId = getCurrentConversationId();
        conversationResult = await createConversation(routingAttributes, storedConversationId);
        
        // 🆕 Try to fetch history from 2 most recent conversations even in fallback
        try {
          const { fetchRecentConversationsHistory } = await import('./conversationService');
          const historyResult = await fetchRecentConversationsHistory();
          
          if (historyResult.messages?.length > 0) {
            conversationResult.existingMessages = historyResult.messages;
            console.log('[Salesforce:Service] 📚 Fallback: Loaded', historyResult.messages.length, 
              'messages from', historyResult.conversationDetails?.length || 0, 'recent conversations');
          }
        } catch (historyError) {
          console.warn('[Salesforce:Service] Fallback: Could not fetch recent conversations history:', historyError);
          // Leave existingMessages undefined
        }
      }
      }
      
      // Clear Last-Event-ID for connection
      const lastEventIdBefore = getLastEventId();
      if (lastEventIdBefore) {
        clearLastEventId();
      }
      
      // Start EventSource connection for real-time updates
      try {
        const forceFresh = this.needsFreshConnection;
        this.startEventSourceConnection(forceFresh);
        this.needsFreshConnection = false;
      } catch (eventSourceError) {
        console.error('[Salesforce:Service] Failed to start EventSource connection:', eventSourceError);
      }
      
      
      // ✅ NEW: Start in CONNECTING state, event handlers will determine actual state
      // ROUTING_RESULT events will move to WAITING_FOR_AGENT or ACTIVE based on agent availability
      this.conversationStatus = CONVERSATION_STATUS.CONNECTING;
      
      return conversationResult;
    } catch (error) {
      console.error('[Salesforce:Service] Failed to start conversation:', error);
      throw error;
    }
  }

  /**
   * Send a message in the current conversation
   */
  async sendMessage(messageText) {
    try {
      if (!this.isInitialized) {
        throw new Error('Salesforce service not initialized');
      }

      if (!hasActiveConversation()) {
        throw new Error('No active conversation for sending message');
      }

      const result = await conversationSendMessage(messageText);
      return result;
    } catch (error) {
      console.error('[Salesforce:Service] Failed to send message:', error);
      throw error;
    }
  }

  /**
   * Send typing indicator
   */
  async sendTypingIndicator(isTyping = true) {
    try {
      if (!this.isInitialized || !hasActiveConversation()) {
        return; // Silently return for typing indicators
      }

      await sendTypingIndicator(isTyping);
    } catch (error) {
      // Don't throw for typing indicators
    }
  }

  /**
   * Start EventSource connection for real-time events
   * @param {boolean} forceFresh - If true, create fresh connection without Last-Event-ID
   */
  startEventSourceConnection(forceFresh = false) {
    if (!this.isInitialized) {
      console.warn('[Salesforce:Service] Cannot start EventSource: Salesforce service not initialized');
      return;
    }

    // Clear Last-Event-ID if forcing fresh connection
    if (forceFresh) {
      clearLastEventId();
    }

    const eventListeners = this.getEventListeners();
    
    subscribeToEventSource(eventListeners, (status) => {
      if (this.connectionStatusCallback) {
        this.connectionStatusCallback(status);
      }
    }, forceFresh);
  }

  /**
   * Get event listeners object for EventSource
   */
  getEventListeners() {
    const listeners = {};
    
    // Convert Map to object for EventSource
    this.eventListeners.forEach((callback, eventType) => {
      listeners[eventType] = callback;
    });

    return listeners;
  }

  /**
   * Add event listener for Salesforce events
   */
  addEventListener(eventType, callback) {
    if (!Object.values(SALESFORCE_EVENT_TYPES).includes(eventType)) {
      console.warn(`[Salesforce:Service] Unknown Salesforce event type: ${eventType}. Valid types:`, Object.values(SALESFORCE_EVENT_TYPES));
    }
    
    this.eventListeners.set(eventType, callback);
  }

  /**
   * Remove event listener
   */
  removeEventListener(eventType) {
    this.eventListeners.delete(eventType);
  }

  /**
   * Set connection status change callback
   */
  onConnectionStatusChange(callback) {
    this.connectionStatusCallback = callback;
  }

  /**
   * Hide chat (back/collapse buttons) - preserve conversation state
   */
  hideChat() {
    // Just hide UI, keep all state intact
  }

  /**
   * Clear chat (X button) - clear conversation for fresh start
   */
  async clearChat() {
    try {
      // Clear conversation storage (this also closes EventSource)
      await this.clearConversationId();
      
      // Clear Last-Event-ID to prevent historical event replay
      clearLastEventId();
      
      // Set flags for fresh connection next time
      this.needsFreshConnection = true;
      this.conversationStatus = CONVERSATION_STATUS.NOT_STARTED;
    } catch (error) {
      console.error('[Salesforce:Service] Failed to clear chat:', error);
      throw error;
    }
  }

  /**
   * End the current conversation
   */
  async endConversation() {
    try {
      // Close EventSource connection
      closeEventSource();
      
      this.conversationStatus = CONVERSATION_STATUS.ENDED;
      this.needsFreshConnection = true; // Next connection should be fresh
    } catch (error) {
      console.error('[Salesforce:Service] Failed to end conversation:', error);
      throw error;
    }
  }

  /**
   * Check if an existing conversation is still active
   */
  async checkConversationStatus(conversationId) {
    try {
      if (!this.isInitialized || !conversationId) {
        return false;
      }

      // For now, check if the conversation ID matches the current stored one
      const currentId = getCurrentConversationId();
      return currentId === conversationId;
    } catch (error) {
      console.error('[Salesforce:Service] Failed to check conversation status:', error);
      return false;
    }
  }

  /**
   * Resume an existing conversation
   */
  async resumeConversation(conversationId) {
    try {
      if (!this.isInitialized) {
        throw new Error('Salesforce service not initialized');
      }
      
      // ✅ NEW: Start in CONNECTING state for resumed conversations
      // Event handlers will determine if agent is present or we need to wait
      this.conversationStatus = CONVERSATION_STATUS.CONNECTING;
      
      // Start EventSource connection for real-time updates
      this.startEventSourceConnection();
      
      return {
        conversationId,
        success: true,
        isResumed: true
      };
    } catch (error) {
      console.error('[Salesforce:Service] Failed to resume conversation:', error);
      throw error;
    }
  }

  /**
   * Clear stored conversation ID and close EventSource connection
   * ✅ CRITICAL: Always close EventSource when clearing conversation to prevent:
   * - Stale connections attempting to reconnect
   * - Cross-conversation event replay
   * - Memory leaks from orphaned connections
   */
  async clearConversationId() {
    try {
      // Clear conversation ID from storage
      const { clearConversationId } = await import('./webStorageService');
      clearConversationId();
      
      // Close EventSource connection to prevent reconnection
      closeEventSource();

      // ✅ NOTE: Do NOT stop network monitoring here!
      // Network monitoring should be managed by the component, not the service.
      // Stopping it here prevents reconnection when network is restored.
      // The component will handle cleanup when appropriate.
      
      console.log('[Salesforce:Service] Conversation ID cleared, EventSource closed.');
    } catch (error) {
      console.error('[Salesforce:Service] Failed to clear conversation ID:', error);
    }
  }

  /**
   * Shutdown Salesforce service and clean up
   */
  async shutdown() {
    try {
      // End conversation if active
      if (this.conversationStatus === CONVERSATION_STATUS.ACTIVE) {
        await this.endConversation();
      }
      
      // Clear event listeners
      this.eventListeners.clear();
      this.connectionStatusCallback = null;
      
      // Clear authentication
      clearAuthentication();
      
      this.isInitialized = false;
      this.conversationStatus = CONVERSATION_STATUS.NOT_STARTED;
      this.needsFreshConnection = false;
    } catch (error) {
      console.error('[Salesforce:Service] Error during Salesforce service shutdown:', error);
    }
  }

  /**
   * Clear all Salesforce data (for logout/reset)
   */
  clearAllData() {
    this.shutdown();
    clearSalesforceWebStorage();
  }

  /**
   * Set conversation status (for external updates)
   */
  setConversationStatus(status) {
    this.conversationStatus = status;
    
    // Set flag for fresh connection when conversation ends
    if (status === CONVERSATION_STATUS.ENDED) {
      this.needsFreshConnection = true;
    }
  }

  /**
   * Get current status information
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      conversationStatus: this.conversationStatus,
      connectionStatus: getConnectionStatus(),
      hasActiveConversation: hasActiveConversation(),
      conversationId: getCurrentConversationId(),
      hasValidJwt: !!getCurrentJwt(),
      isEventSourceConnected: isConnected()
    };
  }

  /**
   * Check if service is ready for messaging
   */
  isReady() {
    return this.isInitialized && 
           hasActiveConversation() && 
           this.conversationStatus === CONVERSATION_STATUS.ACTIVE;
  }
}

// Export singleton instance
export default new SalesforceService();

// Export class for testing or multiple instances if needed
export { SalesforceService };